import clsx from "clsx";
import { XCircle } from "phosphor-react";
import { useEffect, useState, useMemo } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import { ReactComponent as ExportSvg } from "../../../assets/svgs/export.svg";
import { ReactComponent as SettingsSvg } from "../../../assets/svgs/settings.svg";
import { Button } from "../../../components/Button";
import { ConfigureTableColumnsMenu } from "../../../components/ConfigureTableColumnsMenu";
import { SearchInput } from "../../../components/SearchInput";
import { Spinner } from "../../../components/Spinner";
import { type Column } from "../../../components/Table";
import { ColumnFilterValuesMenu } from "../../../components/Table/Filters/ColumnFilterValuesMenu";
import { Medias } from "../../../enumns/medias";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { api } from "../../../services/api";
import { useExpertsQuery } from "../../../services/hooks/experts/queries/useExpertQuery";
import { formatDate } from "../../../utils/date";
import { getQueryStrinfied } from "../../../utils/query";
import { ViewExpertsWithInfiniteScroll } from "./components/ViewExpertsWithInfiniteScroll";
import { ViewExpertsWithManualPagination } from "./components/ViewExpertsWithManualPagination";
import { useAuth } from "../../../contexts/AuthContext";

export type Filters = {
  page: number;
  take: number;
  search: string;
  [key: string]: any;
};

export type GetColumnValuesResponse = {
  id: number;
  value: string;
};

export const DatabasePage = () => {
  const { formatMessage } = useIntl();
  const { user } = useAuth();
  const storageFilters = localStorage.getItem("filters");
  const storageDatabaseInfoPerPageNumber = localStorage.getItem(
    "databaseInfoPerPageNumber"
  );

  const take = storageDatabaseInfoPerPageNumber
    ? Number(JSON.parse(storageDatabaseInfoPerPageNumber))
    : 10;

  const [filters, setFilters] = useState<Filters>(
    storageFilters
      ? JSON.parse(storageFilters)
      : {
          page: 1,
          take: storageDatabaseInfoPerPageNumber
            ? Number(JSON.parse(storageDatabaseInfoPerPageNumber))
            : 10,
          search: "",
          sort: "createdAt",
          order: "DESC",
          hasAcceptedTerms: true,
          shouldShow: true,
        }
  );

  const { isRefetching, isFetching } = useExpertsQuery(filters);
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`);

  const [isExportingToCSV, setIsExportingToCSV] = useState(false);
  const [isTableColumnsConfigMenuOpen, setIsTableColumnsConfigMenuOpen] =
    useState(false);
  const [columnsVisibility, setColumnsVisibility] = useState<Record<string, boolean>>({});

  const handleApplyColumnFilter = (
    filterName: string,
    filteredValues: string[] | boolean[]
  ) => {
    setFilters((prevState) => {
      const newFilters = {
        ...prevState,
        [filterName]: filteredValues.length === 0 ? [""] : filteredValues,
        page: 1,
      };
      
      localStorage.setItem("filters", JSON.stringify(newFilters));
      
      return newFilters;
    });
  };

  const handleClearColumnFilter = (filterName: string) => {
    setFilters((prevState) => {
      if (prevState[filterName]) {
        const storageFilters = localStorage.getItem("filters")
          ? JSON.parse(String(localStorage.getItem("filters")))
          : undefined;
        if (storageFilters && Object.hasOwn(storageFilters, filterName)) {
          // eslint-disable-next-line
          delete storageFilters[filterName];

          localStorage.setItem("filters", JSON.stringify(storageFilters));

          return storageFilters;
        }

        return {
          ...prevState,
          page: 1,
        };
      }

      return prevState;
    });
  };

  const handleProactiveColumnValue = (value: string) => {
    if (value === "no") return "Não";
    if (value === "yesSearch") return "Sim, pesquisa";
    if (value === "yesOffer") return "Sim, oferta";
    return value;
  };

  const handleValueColumnValue = (value: string | null | undefined) => {
    if (value === "") return "0";
    if (value === null) return "0";
    if (value === undefined) return "0";

    return value;
  };

  const parsedStorageFilters = storageFilters
    ? JSON.parse(storageFilters)
    : undefined;

  const clearAllFiltersAndReloadTable = () => {
    // Limpar cada filtro individual (para resetar os checkboxes)
    const defaultKeys = [
      "page",
      "take",
      "search",
      "sort",
      "order",
      "hasAcceptedTerms",
      "shouldShow",
    ];
    if (parsedStorageFilters) {
      Object.keys(parsedStorageFilters).forEach((key) => {
        if (!defaultKeys.includes(key)) {
          handleClearColumnFilter(key);
        }
      });
    }
  };

  const columns = useMemo<Column[]>(() => [
    {
      field: "id",
      headerName: "Id",
      isVisible: true,
    },
    {
      field: "name",
      headerName: "Nome",
      isSticky: true,
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        const selectedValues = filters?.names && filters.names[0] !== "" ? filters.names : [];
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="name-column-values"
            onClearFilter={() => handleClearColumnFilter("names")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("names", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/get-column-values?column=name&hasAcceptedTerms=true&shouldShow=true&page=${page}&take=${take}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={selectedValues}
          />
        );
      },
    },
    {
      field: "lastContactAt",
      headerName: "Último contato",
      isSticky: true,
      isVisible: false,
    },
    {
      field: "agreeParticipateMeet",
      headerName: "Aceita participar de reunião",
      isSticky: true,
      isVisible: false,
    },
    {
      field: "doNotContactTemporarily",
      headerName: "Bloqueado para contato temporário",
      isSticky: true,
      isVisible: false,
    },
    {
      field: "ra",
      headerName: "RA",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="researchers-column-values"
            onClearFilter={() => handleClearColumnFilter("researchers")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("researchers", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/researchers/get-column-values?column=name&page=${page}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={filters?.researchers ?? []}
          />
        );
      },
    },
    {
      field: "acceptedAt",
      headerName: "Data de assinatura",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="created-at-column-values"
            onClearFilter={() => handleClearColumnFilter("creationDates")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("creationDates", filteredValues)
            }
            onClose={onCloseMenu}
            searchInputMask="99/99/9999"
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/get-column-values?column=createdAt&hasAcceptedTerms=true&shouldShow=true&page=${page}&search=${search}`;
              const response = await api.get(url);
              return {
                ...response.data,
                data: response.data.data.map(
                  (item: { id: number; value: string }) => ({
                    ...item,
                    value: formatDate(new Date(item.value)),
                  })
                ),
              };
            }}
            selectedValues={filters?.creationDates ?? []}
          />
        );
      },
    },
    {
      field: "lastContactAt",
      headerName: "Último Contato",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="last-contact-at-column-values"
            onClearFilter={() => handleClearColumnFilter("lastContactDates")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("lastContactDates", filteredValues)
            }
            onClose={onCloseMenu}
            searchInputMask="99/99/9999"
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/get-column-values?column=lastContactAt&hasAcceptedTerms=true&shouldShow=true&page=${page}&search=${search}`;
              const response = await api.get(url);
              return {
                ...response.data,
                data: response.data.data.map(
                  (item: { id: number; value: string }) => ({
                    ...item,
                    value: item.value,
                  })
                ),
              };
            }}
            selectedValues={filters?.lastContactDates ?? []}
          />
        );
      },
    },
    {
      field: "previousCompany",
      headerName: "Empresa anterior",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="previous-company-column-values"
            onClearFilter={() => handleClearColumnFilter("previousCompanies")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("previousCompanies", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/professional-data/get-column-values?column=previousCompany&shouldShow=true&hasAcceptedTerms=true&page=${page}&take=${take}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={filters?.previousCompanies ?? []}
          />
        );
      },
    },
    {
      field: "currentCompany",
      headerName: "Empresa atual",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="current-company-column-values"
            onClearFilter={() => handleClearColumnFilter("currentCompanies")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("currentCompanies", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/professional-data/get-column-values?column=currentCompany&shouldShow=true&hasAcceptedTerms=true&page=${page}&take=${take}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={filters?.currentCompanies ?? []}
          />
        );
      },
    },
    {
      field: "position",
      headerName: "Cargo",
      hasMenuFilter: true,
      maxWidthInPx: 250,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="position-column-values"
            onClearFilter={() => handleClearColumnFilter("positions")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("positions", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/professional-data/get-column-values?column=position&shouldShow=true&hasAcceptedTerms=true&page=${page}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={filters?.positions ?? []}
          />
        );
      },
    },
    {
      field: "sector",
      headerName: "Setor",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="sector-column-values"
            onClearFilter={() => handleClearColumnFilter("sectors")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("sectors", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/professional-data/get-column-values?column=sector&shouldShow=true&hasAcceptedTerms=true&page=${page}&take=${take}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={filters?.sectors ?? []}
          />
        );
      },
    },
    {
      field: "value",
      headerName: "Valor",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="value-column-values"
            onClearFilter={() => handleClearColumnFilter("values")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("values", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/professional-data/get-column-values?column=value&shouldShow=true&hasAcceptedTerms=true&page=${page}&take=${take}&search=${search}`;
              const response = await api.get(url);

              return {
                ...response.data,
                data: response.data.data.map(
                  (item: { id: number; value: string }) => ({
                    ...item,
                    value: handleValueColumnValue(item.value),
                  })
                ),
              };
            }}
            selectedValues={filters?.values ?? []}
          />
        );
      },
    },
    {
      field: "participantsAmount",
      headerName: "Qtde Participantes",
    },
    {
      field: "proactive",
      headerName: "Proativo",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            hasEnableSearch={false}
            cacheKey="proactive-column-values"
            onClearFilter={() => handleClearColumnFilter("proactive")}
            onApplyFilter={(values) => {
              handleApplyColumnFilter(
                "proactive",
                values.map((value) => {
                  if (value === "Não") return "no";
                  if (value === "Sim, pesquisa") return "yesSearch";
                  if (value === "Sim, oferta") return "yesOffer";
                  return value;
                })
              );
            }}
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/professional-data/get-column-values?column=proactive&page=${page}&search=${search}`;
              const response = await api.get(url);
              return {
                ...response.data,
                data: response.data.data.map(
                  (item: { id: number; value: string }) => ({
                    ...item,
                    value: handleProactiveColumnValue(item.value),
                  })
                ),
              };
            }}
            selectedValues={filters?.proactive ?? []}
          />
        );
      },
    },
    {
      field: "email",
      headerName: "E-mail",
    },
    {
      field: "phone",
      headerName: "Telefone",
    },
    {
      field: "cpf",
      headerName: "CPF",
    },
    {
      field: "meetingLanguages",
      headerName: "Idiomas de reunião",
    },
    {
      field: "nationality",
      headerName: "Nacionalidade",
    },
    {
      field: "bankCountry",
      headerName: "País do banco",
    },
    {
      field: "bank",
      headerName: "Banco",
    },
    {
      field: "bankBranch",
      headerName: "Agência",
    },
    {
      field: "currentAccount",
      headerName: "Conta corrente",
    },
    {
      field: "bankDataInternationalPerson",
      headerName: "Dados bancários pessoa internacional",
    },
    {
      field: "rg",
      headerName: "RG",
    },
    {
      field: "birthDate",
      headerName: "Data de nascimento",
    },
    {
      field: "street",
      headerName: "Rua",
    },
    {
      field: "addressNumber",
      headerName: "Número",
    },
    {
      field: "neighborhood",
      headerName: "Bairro",
    },
    {
      field: "city",
      headerName: "Cidade",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="city-column-values"
            onClearFilter={() => handleClearColumnFilter("cities")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("cities", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/natural-data/get-column-values?column=city&page=${page}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={filters?.cities ?? []}
          />
        );
      },
    },
    {
      field: "state",
      headerName: "Estado",
      hasMenuFilter: true,
      renderMenuFilter({ isMenuOpen, dataTrigger, onCloseMenu }: { isMenuOpen: boolean; dataTrigger: string; onCloseMenu: () => void }) {
        return (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey="state-column-values"
            onClearFilter={() => handleClearColumnFilter("states")}
            onApplyFilter={(filteredValues) =>
              handleApplyColumnFilter("states", filteredValues)
            }
            onClose={onCloseMenu}
            onFetchColumnValues={async (page: number, search: string) => {
              const url = `/experts/natural-data/get-column-values?column=state&page=${page}&search=${search}`;
              const response = await api.get(url);
              return response.data;
            }}
            selectedValues={filters?.states ?? []}
          />
        );
      },
    },
    {
      field: "cep",
      headerName: "CEP",
    },
    {
      field: "howDiscoverUs",
      headerName: "Como nos descobriu",
      renderCell: ({ howDiscoverUs }: { howDiscoverUs?: string }) => (
        <div>{howDiscoverUs ? formatMessage({ id: howDiscoverUs }) : "-"}</div>
      ),
    },
    {
      field: "observations",
      headerName: "Observação",
    },
  ].map(column => ({
    ...column,
    isVisible: columnsVisibility[column.field] !== undefined ? columnsVisibility[column.field] : column.isVisible
  })), [filters, columnsVisibility]);

  const handleConfigureColumns = (newColumns: Column[]) => {
    const newVisibility: Record<string, boolean> = {};
    newColumns.forEach(column => {
      newVisibility[column.field] = column.isVisible !== false;
    });
    setColumnsVisibility(newVisibility);
  };

  const handleExportExpertsCsv = () => {
    setIsExportingToCSV(true);

    const visibleColumns = columns
      .filter((column) => column.isVisible !== false)
      .map((column) => column.field);

    const query = getQueryStrinfied({
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value)
      ),
      columns: visibleColumns.join(","),
    });

    api
      .get(`/experts/export/experts-csv?${query}`)
      .then((result) => {
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", "experts.csv");
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => {
        toast(
          formatMessage({
            id: "AN_ERROR_OCCURRED_WHILE_TRYING_TO_EXPORT_THE_EXPERTS_CSV_FILE",
          }),
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            icon: (
              <XCircle size={48} className="mx-auto my-auto text-alert-error" />
            ),
            className: "bg-red-50",
          }
        );
      })
      .finally(() => {
        setIsExportingToCSV(false);
      });
  };

  const onSearchExperts = (therm: string) => {
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: therm,
    }));
  };

  useEffect(() => {
    if (!isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        page: 1,
      }));
    }
  }, [isMinScreenWidthMD]);

  return (
    <div className="py-10">
      <header className="flex flex-col px-6 mb-6 lg:flex-row lg:justify-between">
        <div className="flex items-center justify-between mb-4 lg:gap-3 lg:flex-row lg:mb-0">
          <h1 className="text-base font-medium lg:text-lg text-grey-900 ">
            Base de dados
          </h1>
          {(isFetching || isRefetching) && <Spinner size="small" />}
        </div>

        <div className="flex flex-col items-center gap-4 md:flex-row lg:flex-row">
          <div className="w-full grow lg:w-[493px]">
            <SearchInput
              placeholder="Buscar por palavra-chave"
              onSearch={onSearchExperts}
              className="w-full"
            />
          </div>

          <div className="flex w-full gap-2 md:max-w-sm">
            <div className="relative flex-1">
              <Button
                variant="outline"
                className={clsx({
                  "bg-[#C9D8E1]": isTableColumnsConfigMenuOpen,
                })}
                fullWidth
                onClick={() => setIsTableColumnsConfigMenuOpen(true)}
              >
                <SettingsSvg />
                <span className="text-sm whitespace-nowrap">
                  Configurar tabela
                </span>
              </Button>

              <ConfigureTableColumnsMenu
                columns={columns}
                isOpen={isTableColumnsConfigMenuOpen}
                onClose={() => setIsTableColumnsConfigMenuOpen(false)}
                onConfigureColumns={handleConfigureColumns}
              />
            </div>

            {parsedStorageFilters &&
              Object.keys(parsedStorageFilters).some(
                (key) =>
                  ![
                    "page",
                    "take",
                    "search",
                    "sort",
                    "order",
                    "hasAcceptedTerms",
                    "shouldShow",
                  ].includes(key)
              ) && (
                <button
                  className="text-xs underline text-primary-light"
                  type="button"
                  onClick={() => clearAllFiltersAndReloadTable()}
                >
                  Limpar todos os filtros
                </button>
              )}

            {user?.type === "admin" && (
              <div className="relative flex-1">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleExportExpertsCsv}
                  isLoading={isExportingToCSV}
                >
                  <ExportSvg />
                  <span className="text-sm whitespace-nowrap">
                    Exportar CSV
                  </span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="pl-6 h-full">
        {isMinScreenWidthMD ? (
          <ViewExpertsWithManualPagination
            columns={columns}
            filters={filters}
            setFilters={setFilters}
            showExpertBioButton={true}
          />
        ) : (
          <ViewExpertsWithInfiniteScroll
            columns={columns}
            filters={filters}
            showExpertBioButton={true}
          />
        )}
      </div>
    </div>
  );
};
